/**
 * routePolicy — Surface Layer (/01)
 *
 * Canonical public / auth / plane route policy: the single source of truth for
 * which routes are reachable without an identity, which render without the
 * product shell, the post-auth landing, and which routes are plane-restricted.
 *
 * Target IA (REQ-001.84):
 *   `/`          public landing (anon) → authenticated users redirect to /dashboard
 *   `/login`     global login (canonical); `/sign-in` retained as an alias
 *   `/dashboard` protected, plane-split authenticated landing
 *   `/control-plane` (DevHub) — operator-only;  `/tenant-plane` (UserHub) — members
 */

import { canAccessPlane, type HubPlane } from './hubPlane';

/** Global login route (canonical). `/sign-in` is retained as a working alias. */
export const LOGIN_ROUTE = '/login';
/** Backward-compatible alias kept live so existing links never break. */
export const LOGIN_ALIAS = '/sign-in';
/** Protected authenticated landing — the plane-split dashboard. */
export const DASHBOARD_ROUTE = '/dashboard';

/** Login surfaces (either path renders the sign-in page). */
const LOGIN_ROUTES: ReadonlySet<string> = new Set([LOGIN_ROUTE, LOGIN_ALIAS]);

/** Prefixes reachable WITHOUT an identity. `/` is public; the rest match by prefix. */
const PUBLIC_PREFIXES = ['/login', '/sign-in', '/auth', '/support'];

/** Routes that render without the product shell (public or self-chromed steps). */
const NO_SHELL_EXACT: ReadonlySet<string> = new Set([
  '/',
  '/login',
  '/sign-in',
  '/onboarding',
  '/step-up',
  '/support',
]);

/** The public landing path. */
export const PUBLIC_LANDING = '/';

export function isLoginRoute(pathname: string): boolean {
  return LOGIN_ROUTES.has(pathname);
}

export function isPublicRoute(pathname: string): boolean {
  if (pathname === PUBLIC_LANDING) return true;
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function isNoShellRoute(pathname: string): boolean {
  return NO_SHELL_EXACT.has(pathname) || pathname.startsWith('/auth');
}

/** Plane-restricted route roots — a deep-link here requires the plane capability. */
const PLANE_RESTRICTED: { prefix: string; plane: HubPlane }[] = [
  { prefix: '/control-plane', plane: 'devhub' },
  { prefix: '/devhub', plane: 'devhub' },
  { prefix: '/runtime-cockpit', plane: 'devhub' },
  { prefix: '/tenant-plane', plane: 'userhub' },
];

/** The plane a route requires, or null when the route is plane-agnostic. */
export function requiredPlaneFor(pathname: string): HubPlane | null {
  const match = PLANE_RESTRICTED.find((r) => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`));
  return match ? match.plane : null;
}

/**
 * True when a fully-authenticated user is hitting a plane-restricted route they
 * lack the capability for (e.g. a member deep-linking `/control-plane`). The
 * caller redirects to the dashboard, which renders the user's own plane — never
 * a cross-hub leak.
 */
export function isPlaneAccessBlocked(pathname: string, roles: readonly string[]): boolean {
  const required = requiredPlaneFor(pathname);
  if (!required) return false;
  return !canAccessPlane(roles, required);
}
